import {
  SwitchButton,
  SwitchField,
  type SwitchFieldProps,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

import type { SlotClassNames } from "./types";

const switchStyles = tv({
  slots: {
    base: `
      group inline-flex items-center rounded-full outline-none
      data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60 
      data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-primary/40
      data-[selected=true]:bg-background/0 bg-tertiary cursor-pointer
    `,
    track: `
      relative inline-flex shrink-0 rounded-full transition-colors
      group-data-[selected=true]:bg-primary   
    `,
    thumb: `
      rounded-full group-data-[selected=true]:bg-background/80 bg-gray transition-transform
      group-data-[selected=false]:translate-x-0
    `,
  },
  variants: {
    size: {
      sm: {
        track: "h-6 w-12 px-0.5 py-0.5",
        thumb: "size-5 group-data-[selected=true]:translate-x-6",
      },
      md: {
        track: "h-8 w-14 px-0.5 py-0.5",
        thumb: "size-7 group-data-[selected=true]:translate-x-6",
      },
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

export interface SwitchProps
  extends
    Omit<SwitchFieldProps, "children" | "className">,
    VariantProps<typeof switchStyles> {
  className?: string;
  classNames?: SlotClassNames<ReturnType<typeof switchStyles>>;
}

export default function Switch({
  size,
  className,
  classNames,
  ...props
}: Readonly<SwitchProps>) {
  const styles = switchStyles({ size });

  return (
    // the field div is display:contents so the button label stays the visual
    // root, matching the DOM the deprecated single-element Switch produced
    <SwitchField {...props} className="contents">
      <SwitchButton
        className={styles.base({
          className: `${className ?? ""} ${classNames?.base ?? ""}`.trim(),
        })}
      >
        <span className={styles.track({ className: classNames?.track })}>
          <span className={styles.thumb({ className: classNames?.thumb })} />
        </span>
      </SwitchButton>
    </SwitchField>
  );
}
